'use client'

import { useEffect, useMemo, useState } from 'react'

import { getScrollParent } from '@/lib/scroll-parent'

/**
 * Distance (in CSS pixels) ahead of the scroll container at which a gated
 * element is treated as visible.
 *
 * Relationship with message-list virtualization (keep these reconciled):
 * virtualization decides WHICH rows are mounted (react-virtuoso
 * ``increaseViewportBy``, planned 1200px above / 600px below); this margin
 * decides WHEN an already-mounted card starts its fetch. The two compose, so
 * the effective prefetch window is roughly ``overscan + this margin``.
 *
 * The invariant is ``IN_VIEW_ROOT_MARGIN_PX <= overscan``. If it ever exceeds
 * the overscan the gate stops biting: rows outside the overscan have no
 * mounted observer at all, and every row that does mount is already inside a
 * margin this large, so each newly mounted row fetches immediately. The
 * virtualization PR must import this constant rather than restating 256.
 *
 * For the composition above to be meaningful both numbers have to be measured
 * against the same box, which is why {@link observeInView} resolves the real
 * scroll container as the observer root instead of using the implicit
 * viewport — see getScrollParent for why an implicit root silently zeroes
 * this margin inside an overflow container.
 */
export const IN_VIEW_ROOT_MARGIN_PX = 256

const DEFAULT_ROOT_MARGIN = `${IN_VIEW_ROOT_MARGIN_PX}px`

/**
 * Imperative core: call ``onEnter`` the first time ``element`` comes within
 * ``rootMargin`` of its scroll container, then stop observing. Returns a
 * disconnect function.
 *
 * The observer root is the element's nearest scrollable ancestor, falling
 * back to the viewport for document-scrolled pages. This matters: the chat
 * timeline, the artifacts panel, and the landing analysis panel all scroll
 * inside `overflow-y-auto` containers, and an implicit viewport root would
 * make ``rootMargin`` contribute nothing there.
 *
 * Eager fallback: where ``IntersectionObserver`` does not exist (server
 * render of a client component, the ``tsx --test`` / jsdom unit runners, very
 * old browsers) the element can never be reported as visible, so gating there
 * would strand the content forever. Those environments get ``onEnter``
 * immediately, i.e. today's ungated behaviour.
 *
 * Exported separately from the hook so the gating decision is testable
 * without a DOM renderer.
 */
export function observeInView(
  element: Element,
  onEnter: () => void,
  rootMargin: string = DEFAULT_ROOT_MARGIN,
): () => void {
  if (typeof IntersectionObserver === 'undefined') {
    onEnter()
    return () => {}
  }
  // `disconnect()` already stops further callbacks, but the at-most-once
  // guarantee is part of this function's contract rather than something
  // callers should have to re-derive from the observer's behaviour, so latch
  // it here too.
  let fired = false
  const observer = new IntersectionObserver(
    entries => {
      if (fired) return
      if (entries.some(entry => entry.isIntersecting)) {
        fired = true
        onEnter()
        observer.disconnect()
      }
    },
    { root: getScrollParent(element), rootMargin },
  )
  observer.observe(element)
  return () => observer.disconnect()
}

/**
 * Element-lifecycle wrapper around {@link observeInView}, and the whole of
 * {@link useInView}'s behaviour minus the React state cell.
 *
 * ``attach`` is shaped as a React callback ref: React hands it the node when
 * the gated element mounts and ``null`` when it unmounts, so the observer is
 * wired exactly when there is something to observe. That is deliberately not
 * a ``RefObject`` — with a ref object the effect reads ``ref.current`` once,
 * and any future edit that stops rendering the ref'd element while gated
 * leaves ``current`` null forever with no observer, no error, and a thumbnail
 * stuck on its placeholder. Here a later attach simply re-wires.
 *
 * Split out from the hook so the wiring (observe on attach, disconnect on
 * detach, never re-observe after entering) is testable under a plain node
 * runner with no DOM.
 */
export function createInViewBinding<T extends Element>(
  onVisible: () => void,
  rootMargin: string = DEFAULT_ROOT_MARGIN,
): { attach: (node: T | null) => void; disconnect: () => void } {
  let cleanup: (() => void) | null = null
  let entered = false

  const disconnect = () => {
    cleanup?.()
    cleanup = null
  }

  return {
    attach(node) {
      disconnect()
      // Once visible the gate has latched: re-attaching (e.g. the consumer
      // keeps the ref on the loaded branch) must not start a second observer.
      if (!node || entered) return
      cleanup = observeInView(
        node,
        () => {
          entered = true
          onVisible()
        },
        rootMargin,
      )
    },
    disconnect,
  }
}

/**
 * One-way "has this element come near the viewport yet" flag, used to defer
 * expensive work (preview byte fetches, heavy viewer mounts) until the user
 * can plausibly see the result.
 *
 * Attach ``ref`` to an element that is rendered **while gated** — the
 * placeholder. ``inView`` latches: it never flips back to false, so scrolling
 * away does not tear down work that is already in flight or already rendered.
 * Releasing what was loaded is a separate concern owned by the preview
 * memory-budget registry (LRU over unpinned entries), deliberately not done
 * here: evicting on scroll-out would thrash under virtualization.
 *
 * Usage (fetch gate):
 * ```tsx
 * const { ref, inView } = useInView<HTMLDivElement>()
 * const { data } = useFilePreviewText(inView ? url : null)
 * if (!inView) return <div ref={ref}>{placeholder}</div>
 * ```
 */
export function useInView<T extends Element>(
  rootMargin: string = DEFAULT_ROOT_MARGIN,
): { ref: (node: T | null) => void; inView: boolean } {
  // Always starts false, including on the server: the eager fallback lives in
  // the ref attach (which never runs during SSR) rather than in a lazy
  // initializer, so the server's first paint and the client's first paint
  // render the same branch. A lazy initializer would compute `true` on the
  // server — no IntersectionObserver there — and `false` in the browser,
  // which is a hydration mismatch.
  const [inView, setInView] = useState(false)
  const binding = useMemo(
    () => createInViewBinding<T>(() => setInView(true), rootMargin),
    [rootMargin],
  )
  useEffect(() => binding.disconnect, [binding])

  return { ref: binding.attach, inView }
}
